import {useState} from 'react';
import ImageResizer from 'react-image-file-resizer';
import { Media, ResolutionObject } from '../dtos/guest';

const STORAGE_ZONE_NAME = 'momap-storage';
const API_KEY = '6c3f8b50-2c17-40db-9c23fb7909bb-9a12-4dd2';
const CDN_URL = 'https://momap.b-cdn.net';
// Resolution configurations
const RESOLUTIONS = {
  HIGH: 1080,
  MEDIUM: 720,
  LOW: 360,
};

export const useImageUploader = () => {
  const [isUploading, setIsUploading] = useState(false);

  const resizeImage = (
    file: File,
    targetWidth: number,
    quality: number,
    type: string = 'image/jpeg',
  ) => {
    return new Promise<{
    uri: string; 
    width: number; 
    height: number; 
    aspectRatio: '16:9' | '9:16' 
  }>((resolve, reject) => {
    try {
      const img = new Image();
      img.src = URL.createObjectURL(file);

      img.onload = () => {
        const originalWidth = img.width;
        const originalHeight = img.height;
        URL.revokeObjectURL(img.src);

        const ratio = originalWidth / originalHeight;
        let aspectRatio: '16:9' | '9:16'= '9:16';

        // 1. Explicitly check orientation first to prevent mixups
        if (originalWidth >= originalHeight) {
          // Landscape: Check if it is close to 1.777
          // if (Math.abs(ratio - (16 / 9)) < 0.15) {
          //   aspectRatio = '16:9';
          // }
          aspectRatio = '16:9';

        } else {
          // Portrait: Check if it is close to 0.5625
          // if (Math.abs(ratio - (9 / 16)) < 0.15) {
          //   aspectRatio = '9:16';
          // }
        }

        // 2. Set dynamic constraints based on the discovered ratio
        let maxWidth = targetWidth;
        let maxHeight = targetWidth;

        if (aspectRatio === '16:9') {
          maxHeight = Math.round((targetWidth * 9) / 16);
        } else if (aspectRatio === '9:16') {
          // For portrait, the height needs to be larger than the width constraint
          maxHeight = Math.round((targetWidth * 16) / 9);
        } else {
          maxHeight = Math.round(targetWidth / ratio);
        }

        ImageResizer.imageFileResizer(
          file,
          maxWidth,  // Exact boundary width
          maxHeight, // Exact boundary height
          type,
          quality,
          0,
          (result) => {
            resolve({
              uri: result as string,
              width: maxWidth,
              height: maxHeight,
              aspectRatio,
            });
          },
          'base64'
        );
      };

      img.onerror = (err) => reject(err);

    } catch (error) {
      reject(error);
    }
  });

  };

  const uploadToBunny = async (
    uri: string,
    resolution: number,
    type: string = 'image/jpeg'
  ): Promise<string> => {
    const filename = `${Date.now()}_${resolution}_${Math.random()
      .toString(36)
      .substring(7)}.${type.split('/')[1]}`;

    // Use the correct Bunny CDN storage URL format
    const uploadUrl = `https://storage.bunnycdn.com/${STORAGE_ZONE_NAME}/${filename}`;

    // Read the file
    const response = await fetch(uri);
    const blob = await response.blob();

    // Use the same headers format as the video uploader
    const headers = {
      'Accept': 'application/json',
      'AccessKey': API_KEY,
      'Content-Type': 'application/octet-stream',
    };

    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers,
      body: blob,
    });

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      throw new Error(`Upload failed: ${uploadResponse.status} - ${errorText}`);
    }

    // Return the CDN URL for the uploaded file
    return `${CDN_URL}/${filename}`;
  };

  const processResolution = async (
    file: File,
    width: number,
    quality: number,
    isPrimary: boolean,
  ): Promise<ResolutionObject> => {
    try {
      const typeP =file.type ?? 'image/jpeg' ; // Changed to JPEG for better compatibility
      const resized = await resizeImage(file, width, quality, typeP);
      const uploadedUrl = await uploadToBunny(resized.uri, width, typeP);

      return {
        mime_type: typeP,
        name:
          width === RESOLUTIONS.HIGH
            ? 'high'
            : width === RESOLUTIONS.MEDIUM
            ? 'medium'
            : 'low',
        aspect_ratio: resized.aspectRatio,
        url: uploadedUrl,
        primary: isPrimary,
      };
    } catch (error) {
      throw error; // Re-throw to be handled by the caller
    }
  };

  const uploadImage = async (file: File): Promise<Media> => {
    setIsUploading(true);

    try {
      // Process all resolutions in parallel
      const resolutions = await Promise.all([
        processResolution(file, RESOLUTIONS.HIGH, 100, true),
        processResolution(file, RESOLUTIONS.MEDIUM, 85, false),
        processResolution(file, RESOLUTIONS.LOW, 80, false),
      ]);

      // Return the correct format with resolutions as an array
      return {
          resolutions: resolutions,
        };
    } catch (error) {
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    uploadImage,
    isUploadingImage: isUploading,
  };
};
